import React, { useState, useEffect,  } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, Loader, CheckCircle, Users, Key, ExternalLink } from "lucide-react";
import { LoadingSpinner } from "../components/common";
import {
  addOAuthClient,
  fetchRolesAndPermissions,
} from "../services/gc/createroleAndOAuthClientService";
import GlobalAuthenticatedContext from "../components/common/contexts/GlobalAuthenticatedContexts";
import config from "../config";
import {
  RoleConstants,
  OrganizationConstants,
} from "../components/common/utilities/appConstants";
import { toast } from 'react-toastify';
import { getSessionStorageItem } from "../components/common/utilities/applicationStorage";
interface SetupProps {
  setupCompleted: boolean;
  setSetupCompleted: (completed: boolean) => void;
}

interface Policy {
  // actionSet: string[];
  // actions: string[];
  domain: string;
  entityName: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  policies: Policy[];
  selected: boolean;
}

interface OAuthClient {
  clientName: string;
  clientId: string;
  grantTypes: string[];
  // scopes: string[];
}

interface ResourceSummary {
  oauthClient: OAuthClient;
  roles: Role[];
}

const Setup: React.FC<SetupProps> = ({ setupCompleted, setSetupCompleted }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [clientName, setClientName] = useState<string>(
    "SharePoint KB Connector"
  );
  const [resourceSummary, setResourceSummary] =
    useState<ResourceSummary | null>(null);

  const [baseUrl, setBaseUrl] = React.useState("");
  const { globalAuthenticated, setGlobalAuthenticated } = React.useContext(
    GlobalAuthenticatedContext
  );
  const installed = globalAuthenticated.installed;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const url = new URL(window.location.href);
        const pathParameters = url.pathname.split("/");
        const newPath = `${pathParameters[1]}`;
        const appUrl = getSessionStorageItem(OrganizationConstants.UrlApps);

        console.log("url", url, pathParameters, newPath);
        setBaseUrl(`${appUrl}/directory/#/admin`);
        const clientId = config.client;
        const { data } = await fetchRolesAndPermissions(clientId);
        
        let filteredRoles: any;
        if (installed) {
          console.log("Roles and permissions", data.results)
          filteredRoles = data?.results?.grants
            .filter((grant: any) => {
              return (
                RoleConstants.admin === grant.role.name ||
                RoleConstants.sharepointAdmin === grant.role.name ||
                RoleConstants.sharepointView === grant.role.name
              );
            })
            .map((item: any) => {
              if (item.role) {
                // eslint-disable-next-line
                const { default: _, selfUri, ...cleanedRole }: any = item.role;
                return cleanedRole;
              }
              return null;
            })
            .filter((role: any) => role !== null);

            setGlobalAuthenticated((prev: any) => ({
          ...prev,
          userRoles: filteredRoles
        }));
          // eslint-disable-next-line
          // scopes = [...data.results.scopeDetails.scope];
          console.log("Filtered roles got from backend", filteredRoles);
        } else {
          // Show roles and permissions before creating
          filteredRoles = config.app.appFoundry.provisioningInfo.roles.map(
            (r: Role) => ({
              ...r,
              selected: true,
            })
          );
        }

        const summary: ResourceSummary = {
          oauthClient: {
            clientName: clientName || "SharePoint KB Connector",
            clientId: data.results.clientID || '',
            grantTypes: ["CLIENT-CREDENTIALS"],
            // scopes: data.results.scopeDetails.scope,
          },
          roles: [...filteredRoles],
        };
        setResourceSummary(summary);
      } catch (error: any) {
        console.log(`Failed to fetch OAuth client details`, error);
        if (error.status === 401 ) navigate("/unauthorized");
        // else navigate("/error", { state: { error: data } });
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
    // eslint-disable-next-line
  }, [globalAuthenticated?.user.id, installed]);

  const handleClientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedClientName = e.target.value;
    setClientName(updatedClientName);
 
    setResourceSummary((prevState: any) => { 
      return {
        ...prevState,
        oauthClient: {
          ...prevState.oauthClient,
          clientName: updatedClientName
        }
      }
    });
  };

  const handleRoleToggle = (roleId: string) => {
    setResourceSummary((prevState: any) => {
      if (!prevState || !prevState.roles) {
        return prevState;
      }
      const updatedRoles = prevState.roles.map((role: any) =>
        role.id === roleId ? { ...role, selected: !role.selected } : role
      );
      return {
        ...prevState,
        roles: updatedRoles, // All roles stay here, selected state updated
      };
    });
  };

  const handleSetupComplete = async () => {
    setLoading(true);
    // Simulate API call
    try {
      const selectedRoleNames: any = resourceSummary?.roles.filter((role) => role.selected).map((role) => role.name)
      const { data: clientCreds } = await addOAuthClient(clientName, selectedRoleNames );
      setGlobalAuthenticated((prev: any) => ({
        ...prev,
        installed: clientCreds.response.active,
      }));
      setSetupCompleted(globalAuthenticated.installed);
      const { data } = await fetchRolesAndPermissions(config.client);
      const updatedRoles = data?.results?.grants
          .filter((grant: any) => {
            return (
              RoleConstants.admin === grant.role.name ||
              RoleConstants.sharepointAdmin === grant.role.name ||
              RoleConstants.sharepointView === grant.role.name
            );
          })
          .map((item: any) => {
            if (item.role) {
              // eslint-disable-next-line
              const { default: _, selfUri, ...cleanedRole }: any = item.role;
              return cleanedRole;
            }
            return null;
          })
          .filter((role: any) => role !== null);
      console.log("Updated roles", updatedRoles);
      setGlobalAuthenticated((prev: any) => ({
          ...prev,
          userRoles: [...updatedRoles],
        }));
      setResourceSummary((prev: any) => ({
        ...prev,
        roles: updatedRoles
      }));
      setLoading(false);
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Failed to complete initial setup: ${error.message}`);
      } else {
        toast.error('Unable to load initial setup. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };


  if (loading) {
    return (
      <div className="pt-16 ml-64">
        <div className="p-6">
          <LoadingSpinner
            title="Loading Setup"
            message="Loading resources..."
            size="large"
            fullScreen={false}
            className="py-16"
          />
        </div>
      </div>
    );
  }
  if (installed || setupCompleted) {
    return (
      <div className="pt-16 ml-64">
        <div className="p-6">
          <div className="w-full flex flex-col items-center px-6 py-10 bg-white rounded-xl shadow-md">
            {resourceSummary && (
              <>
                <div className="flex items-center justify-center mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                  <h2 className="text-2xl font-semibold text-gray-900">
                    Setup Successfully Completed
                  </h2>
                </div>

                <div className="flex flex-row justify-between gap-6 w-full max-w-6xl">
                  {/* OAuth Card */}
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <div className="mb-4">
                      <div className="flex items-center mb-1">
                        <Key className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          OAuth Client
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 ml-7">For system-to-system access</p>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm font-medium text-gray-700">Client Name</p>

                         <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {resourceSummary?.oauthClient?.clientName || 'N/A'}
                          </span>
                          <a
                            href={`${baseUrl}/people-permissions/roles/${
                              resourceSummary?.oauthClient?.clientId || 'N/A'
                            }`}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="flex items-center gap-1">
                              View
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          </a>
                         </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Grant Types</p>
                        <p className="text-sm text-gray-600">
                          {resourceSummary?.oauthClient?.grantTypes?.[0] === 'CLIENT-CREDENTIALS' ? 'Client Credentials' : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Primary Role</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                          {resourceSummary?.roles?.find(
                            (role) => role.name === RoleConstants.admin
                          )?.name || 'N/A'}
                          </span>
                          <a
                            href={`${baseUrl}/people-permissions/roles/${
                              resourceSummary?.roles?.find((role) => role.name === RoleConstants.admin)?.id || 'N/A'
                            }`}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <span className="flex items-center gap-1">
                              View
                              <ExternalLink className="w-3 h-3" />
                            </span>
                          </a>
                        </div>
                      </div>
                      
                    </div>
                  </div>

                  {/* Roles Card */}
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg p-6">
                    <div className="mb-4">
                      <div className="flex items-center mb-1">
                        <Users className="w-5 h-5 text-blue-600 mr-2" />
                        <h3 className="text-lg font-medium text-gray-900">
                          Roles
                        </h3>
                      </div>
                      <p className="text-xs text-gray-500 ml-7">Manage people with below roles</p>
                    </div>
                    <div className="space-y-2">
                      {resourceSummary?.roles
                        ?.filter(
                          (role) =>
                            (role.name === RoleConstants.sharepointAdmin ||
                            role.name === RoleConstants.sharepointView)
                        )
                        ?.map((role, i) => (
                          <div key={`${role.name}_${i}`} className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">{role.name}</span>
                            <a
                              href={`${baseUrl}/people-permissions/roles/${role?.id || ''}`}
                              className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span className="flex items-center gap-1">
                                View
                                <ExternalLink className="w-3 h-3" />
                              </span>
                            </a>
                          </div>
                        ))
                      }
  
                      {/* Show 'No roles available' if nothing matches */}
                      {resourceSummary?.roles?.filter(
                        (role) =>
                          (role.name === RoleConstants.sharepointAdmin ||
                          role.name === RoleConstants.sharepointView)
                      ).length === 0 && (
                        <p className="text-sm text-gray-500">No roles available</p>
                      )}
                    </div>
                  </div>

                  {/* Permissions Card */}
      
                </div>

                <div className="flex flex-col items-center justify-center mt-8 text-center">
                  <p className="text-base text-gray-600 mb-6 max-w-lg">
                    Initial setup has been completed successfully. You can now configure
                    SharePoint connections and manage knowledge bases.
                  </p>
                  <Link to="/sharepoint">
                    <button className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Go to SharePoint Configuration
                    </button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 ml-64">
      <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-medium">
                Genesys Cloud Configuration
              </h2>
              <div className="text-sm text-gray-500">
                Step {currentStep} of 3
              </div>
            </div>
            <div className="mt-4 relative">
              <div className="overflow-hidden h-2 flex rounded-full bg-gray-200">
                <div
                  style={{ width: `${(currentStep / 3) * 100}%` }}
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-600 transition-all duration-300"
                ></div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {currentStep === 1 && (
              <div>
                <h3 className="text-lg font-medium mb-4">OAuth Credentials</h3>
                <p className="mb-4 text-gray-600">
                  This step will create the necessary OAuth credentials in
                  Genesys Cloud to allow the integration to access your data.
                </p>
                <div className="bg-blue-50 p-4 rounded-md mb-6">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle
                        className="h-5 w-5 text-blue-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">
                        Information
                      </h3>
                      <div className="mt-2 text-sm text-blue-700">
                        <p>
                          You will need administrator access to your Genesys
                          Cloud organization to complete this step.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Client Name
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      placeholder="SharePoint KB Connector"
                      value={clientName}
                      onChange={handleClientNameChange}
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setCurrentStep(2)}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h3 className="text-lg font-medium mb-4">
                  Roles & Permissions
                </h3>
                <p className="mb-4 text-gray-600">
                  Configure the required roles and permissions for the
                  integration.
                </p>
                <div className="space-y-4 mb-6">
                  {resourceSummary?.roles?.map((role) => (
                    <div key={role.id} className="border rounded-md p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 pt-0.5">
                          <input
                            id={`role-${role.id}`}
                            name={`role-${role.id}`}
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            checked={role.selected}
                            onChange={() => handleRoleToggle(role.id)}
                          />
                        </div>
                        <label
                          htmlFor={`role-${role.id}`}
                          className="ml-3 flex flex-col cursor-pointer"
                        >
                          <span className="text-sm font-medium text-gray-700">
                            {role.name}
                          </span>
                          <span className="text-sm text-gray-500">
                            {role.description}
                          </span>
                        </label>
                      </div>
                    </div>
                  )) || <div>No roles available</div>}
                </div>
                <div className="mt-6 flex justify-end">
                  <button
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setCurrentStep(1)}
                  >
                    Back
                  </button>
                  <button
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setCurrentStep(3)}
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {currentStep === 3 && resourceSummary && (
              <div>
                <h3 className="text-lg font-medium mb-4">Resource Summary</h3>
                <p className="mb-4 text-gray-600">
                  Review the resources that will be created in your Genesys
                  Cloud organization.
                </p>

                <div className="bg-gray-50 p-4 rounded-md mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    OAuth Client
                  </h4>
                  <ul className="ml-4 text-sm text-gray-600 list-disc space-y-1">
                    <li>
                      Client Name: {resourceSummary?.oauthClient?.clientName || 'N/A'}
                    </li>
                    <li>
                      Grant Types:{" "}
                      {resourceSummary?.oauthClient?.grantTypes?.[0] === 'CLIENT-CREDENTIALS' ? 'Client Credentials' : 'N/A'}
                    </li>
                 
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-md mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Roles
                  </h4>
                  <ul className="ml-4 text-sm text-gray-600 list-disc space-y-1">
                    {resourceSummary?.roles?.filter(role => role.selected).length > 0 ? (
                      resourceSummary.roles
                        .filter(role => role.selected)
                        .map((role, index) => (
                          <li key={index}>{role.name}</li>
                        ))
                    ) : (
                      <li>No roles selected</li>
                    )}
                  </ul>
                </div>

                <div className="bg-gray-50 p-4 rounded-md mb-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Permissions
                  </h4>
                  <ul className="ml-4 text-sm text-gray-600 list-disc space-y-1">
                    {resourceSummary?.roles?.map(
                      (role) =>
                        role.policies?.length > 0 &&
                        role.policies?.map((policy, index) => (
                          <li key={index}>
                            {policy.domain} : {policy.entityName}
                          </li>
                        ))
                    ) || <li>No permissions available</li>}
                  </ul>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={() => setCurrentStep(2)}
                  >
                    Back
                  </button>
                  <button
                    className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    onClick={handleSetupComplete}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader size={16} className="mr-2 animate-spin" />
                        Creating Resources...
                      </>
                    ) : (
                      "Complete Setup"
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Setup;
